import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import {
  MediaQuery,
  Chip,
  ExposedFilters,
  FilterUtils,
  Close,
  useFacetSearchContext
} from '@spotahome/ui-library';
import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';
import Bell from '@spotahome/marketplace-common/src/components/icons/Bell';
import AlertButton from '@spotahome/marketplace-common/src/components/AlertButton';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { useFiltersContext } from '@spotahome/ui-library/src/tenant/context/FiltersContext';

import MapListSwitcherDesktop from '@spotahome/ui-library/src/tenant/components/MapListSwitcher/MapListSwitcherDesktop';

import BILLS from '@spotahome/ui-library/src/tenant/components/Filters/utils/bills-config';

import './FiltersExposed.scss';

const BILLS_NAMES = BILLS.map(({ name }) => name);

const noop = () => {};

const FiltersExposed = ({
  cityId,
  currencyIsoCode,
  totalResults,
  onFiltersToggle,
  onFiltersUpdate,
  onFiltersClose,
  onFacetFiltersUpdate,
  maxPriceInCity,
  minimumStaying,
  sortingAlgorithm,
  onClearFilters = noop,
  showMapListSwitcher = false,
  onSwitcherClick
}) => {
  const { filters } = useFiltersContext();
  const { facetSearch, enableFacetSearch } = useFacetSearchContext();
  const { FromLaptop } = MediaQuery;
  const [filtersCount, setFiltersCount] = useState(0);
  const isApplyAllAtOnce =
    useSoyuzExperiment('ApplyAllFiltersAtOncev2').get('behaviour') ===
    'variant';

  const shouldShowClearFiltersButton = useMemo(() => {
    const activeFiltersCount = FilterUtils.getActiveFiltersCount(filters);
    return activeFiltersCount > 0 && totalResults !== 0;
  }, [filters, totalResults]);

  useEffect(() => {
    const filtersWithoutExposedFilters = {
      ...filters,
      'move-in': undefined,
      'move-out': undefined,
      moveOutFrom: undefined,
      moveOutTo: undefined,
      moveInFrom: undefined,
      moveInTo: undefined,
      budget: undefined,
      'rentalType[]': undefined,
      'type[]': undefined,
      'topology[]': undefined,
      'features[]': filters['features[]'].filter(
        feature => !BILLS_NAMES.includes(feature)
      )
    };

    setFiltersCount(
      FilterUtils.getActiveFiltersCount(filtersWithoutExposedFilters)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFullFilterOpen = useCallback(() => {
    SoyuzAnalytics.sendGA4Event('open-exposed-filters', {});
    onFiltersToggle();
  }, [onFiltersToggle]);

  const handleFiltersOpen = useCallback(
    filterName => {
      enableFacetSearch();
      SoyuzAnalytics.sendGA4Event('open-exposed-filters', {
        section: filterName
      });
      onFiltersClose();
    },
    [onFiltersClose, enableFacetSearch]
  );

  const handleFiltersClose = useCallback(filterName => {
    SoyuzAnalytics.sendGA4Event('close-exposed-filters', {
      section: filterName
    });
  }, []);

  const handleShowResultsClick = useCallback(
    filterName => () => {
      SoyuzAnalytics.sendGA4Event('show-results', {
        context: 'exposed-filters',
        section: filterName
      });
    },
    []
  );

  return (
    <div className="filters-exposed">
      <ExposedFilters
        filters={filters}
        cityId={cityId}
        currencyIsoCode={currencyIsoCode}
        totalResults={facetSearch.totalCount}
        onFiltersUpdate={onFiltersUpdate}
        onFacetFiltersUpdate={onFacetFiltersUpdate}
        onFiltersOpen={handleFiltersOpen}
        onFiltersClose={handleFiltersClose}
        onShowResultsClick={handleShowResultsClick}
        maxPriceInCity={maxPriceInCity}
        minimumStaying={minimumStaying}
        sortingAlgorithm={sortingAlgorithm}
        showResults
        applyAllFiltersAtOnce={isApplyAllAtOnce}
      />
      <Chip.Filter
        label={trans('search.filter_header.more_filters')}
        className="filters-exposed__filter-button"
        onClick={handleFullFilterOpen}
        dataTest="more-filters-button"
        isSelected={!!filtersCount}
        renderTrailingIcon={() =>
          filtersCount ? <span>{filtersCount}</span> : null
        }
      />
      {shouldShowClearFiltersButton && (
        <Chip.Filter
          label={trans('search.filters.clear.cta')}
          className="filters-exposed__filter-button filters-exposed__filter-button__close"
          dataTest="title-filters-summary-clear-button"
          onClick={onClearFilters}
          isSelected
          renderTrailingIcon={() => (
            <Close
              size="extra_small"
              theme="interaction"
              className="filters-exposed__filter-button__close-icon"
            />
          )}
        />
      )}
      <FromLaptop>
        <AlertButton
          buttonClassName="filters-exposed__save-search-button"
          buttonProps={{
            size: 'small',
            color: 'primary',
            renderIcon: () => <Bell />
          }}
          cityId={cityId}
          filters={filters}
          dataTest="alert-button-exposed-filters"
        />

        {showMapListSwitcher && (
          <MapListSwitcherDesktop
            className="filters-exposed__map-list-switcher"
            onClickLeft={onSwitcherClick}
            onClickRight={onSwitcherClick}
            isPositionRight={filters.map}
          />
        )}
      </FromLaptop>
    </div>
  );
};

FiltersExposed.propTypes = {
  totalResults: PropTypes.number.isRequired,
  cityId: PropTypes.string.isRequired,
  currencyIsoCode: PropTypes.string.isRequired,
  onFiltersUpdate: PropTypes.func.isRequired,
  onFiltersClose: PropTypes.func.isRequired,
  onFiltersToggle: PropTypes.func.isRequired,
  onFacetFiltersUpdate: PropTypes.func.isRequired,
  maxPriceInCity: PropTypes.number.isRequired,
  minimumStaying: PropTypes.number.isRequired,
  sortingAlgorithm: PropTypes.string.isRequired,
  onClearFilters: PropTypes.func,
  showMapListSwitcher: PropTypes.bool,
  onSwitcherClick: PropTypes.func.isRequired
};

export default memo(FiltersExposed);
